import TrouphySVG from "../../utils/TrouphySVG";
import { motion } from "framer-motion";
import React, { useState } from "react";
import { X } from "lucide-react";
import { useArtistAwards } from "@/api/hooks/useArtistAwards";
import { useAwardsStore } from "@/stores/awardsStore";

interface AchievmentMobileRoadProps {
  enableDelete?: boolean;
}

const AchievmentMobileRoad: React.FC<AchievmentMobileRoadProps> = ({
  enableDelete = false,
}) => {
  const { awards } = useArtistAwards();
  const deleteItem = useAwardsStore((s) => s.deleteItem);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const awards_list = awards.slice(0, 6).map((item) => ({
    id: item.id,
    award_name: item.name,
    description: item.description ?? item.displayText,
    award_year: item.year,
    isPlaceholder: item.isPlaceholder,
  }));
  const totalAwards = awards_list.length;

  const [activeIndex, setActiveIndex] = useState(null);

  const toggleTooltip = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  let displayAwards = [];

  switch (totalAwards) {
    case 3:
      displayAwards = [awards_list[0], awards_list[1], awards_list[2]];
      break;
    case 4:
      displayAwards = [
        awards_list[0],
        awards_list[2],
        awards_list[1],
        awards_list[3],
      ];
      break;
    case 5:
      displayAwards = [
        awards_list[0],
        awards_list[1],
        awards_list[2],
        awards_list[3],
        awards_list[4],
      ];
      break;
    default:
      displayAwards = awards_list;
  }

  const positions = {
    default: [
      { top: "1%", right: "20%" },
      { top: "1%", left: "30%" },
      { top: "36%", right: "22%" },
      { top: "36%", right: "60%" },
      { bottom: "5%", right: "40%" },
      { bottom: "5%", left: "10%" },
    ],
    threeAwards: [
      { top: "1%", right: "20%" },
      { top: "40%", right: "45%" },
      { bottom: "10%", left: "10%" },
    ],
    fourAwards: [
      { top: "1%", right: "20%" },
      { top: "38%", left: "25%" },
      { top: "38%", right: "30%" },
      { bottom: "10%", left: "10%" },
    ],
    fiveAwards: [
      { top: "1%", right: "20%" },
      { top: "1%", left: "28%" },
      { top: "42%", right: "40%" },
      { bottom: "2%", right: "30%" },
      { bottom: "2%", left: "12%" },
    ],
  };

  return (
    <div className="-mt-28 z-40 bg-black rounded-3xl relative md:hidden">
      <svg
        viewBox="0 0 430 248"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="w-full h-auto"
        preserveAspectRatio="xMidYMid meet"
      >
        <path
          d="M362.161 148.273H68.8412C31.4348 148.273 1.0018 115.17 1.0018 74.4814C1.0018 33.793 31.4341 0.689941 68.8412 0.689941H430V48.5443H68.8419C55.6939 48.5443 44.9969 60.1798 44.9969 74.4814C44.9969 88.7831 55.6932 100.418 68.8419 100.418H362.161C399.567 100.418 430 133.52 430 174.208C430 214.897 399.568 248 362.161 248H0V200.146H362.161C375.309 200.146 386.006 188.511 386.006 174.208C386.006 159.906 375.309 148.273 362.161 148.273Z"
          fill="#A5A7A9"
        />
        <path
          d="M390.89 174.21C390.89 156.976 378.001 142.956 362.157 142.956H68.8379C34.127 142.956 5.88669 112.239 5.88669 74.4821C5.88669 36.7258 34.127 6.00781 68.8379 6.00781H430V43.2279H68.8386C52.995 43.2279 40.1053 57.2485 40.1053 74.4821C40.1053 91.7158 52.995 105.736 68.8386 105.736H362.157C396.869 105.736 425.108 136.453 425.108 174.209C425.108 211.966 396.869 242.684 362.157 242.684L0 243.797V205.463H362.157C378.001 205.463 390.89 191.443 390.89 174.21Z"
          fill="black"
        />
        <path
          d="M409.578 24.6177H69.0325C43.6113 24.6177 23.0031 46.9429 23.0031 74.482C23.0031 102.021 43.6113 124.346 69.0325 124.346"
          stroke="white"
          strokeWidth="1.36735"
          strokeMiterlimit="10"
          strokeLinecap="round"
          strokeDasharray="8.2 8.2"
        />
        <path
          d="M23.0031 224.073H363.549C388.97 224.073 409.578 201.748 409.578 174.209C409.578 146.67 388.97 124.345 363.549 124.345H69.0317"
          stroke="white"
          strokeWidth="1.36735"
          strokeMiterlimit="10"
          strokeLinecap="round"
          strokeDasharray="8.2 8.2"
        />
      </svg>
      {/* Render Trophies with dynamic positions */}
      {displayAwards.map((award, index) => {
        const position =
          totalAwards === 3
            ? positions.threeAwards[index]
            : totalAwards === 4
              ? positions.fourAwards[index]
              : totalAwards === 5
                ? positions.fiveAwards[index]
                : positions.default[index];

        // Determine tooltip position: left or right
        const isLeftSide = position?.left !== undefined; // If left is defined, item is on the left side

        return (
          <div key={award?.id} className="absolute" style={position}>
            <div
              onClick={() => toggleTooltip(index)}
              className="cursor-pointer relative"
            >
              <motion.div
                animate={{ y: [0, -4, 0] }}
                transition={{
                  duration: 1,
                  repeat: Infinity,
                  repeatType: "reverse",
                  delay: index * 0.5,
                }}
              >
                <TrouphySVG />
              </motion.div>

              {activeIndex === index && (
                <div
                  className={`absolute -top-20 rounded-lg text-sm text-center bg-pink-900/80 border-2 border-pink-600 p-1 min-w-60 h-20 text-white
              ${isLeftSide ? "left-0 ml-2" : "right-0 mr-2"}`}
                >
                  {award?.description}
                </div>
              )}

              <span className="max-w-24 absolute text-xs text-center h-10 -bottom-12 right-0 -left-10 w-full min-w-40 bg-black/60">
                {enableDelete &&
                  !award?.isPlaceholder &&
                  typeof award?.id === "number" && (
                  <button
                    type="button"
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (deletingId !== null) return;
                      try {
                        setDeletingId(award.id as number);
                        await deleteItem(award.id as number);
                      } finally {
                        setDeletingId(null);
                      }
                    }}
                    disabled={deletingId === award.id}
                    className="absolute -top-3 right-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/80 text-[10px] text-white border border-white/40 disabled:opacity-60"
                  >
                    {deletingId === award.id ? "…" : <X className="h-3 w-3" />}
                  </button>
                )}
                {award?.award_name}
              </span>
              <span className="absolute top-2 px-1 -right-10 bg-black/60">
                {award?.award_year}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default AchievmentMobileRoad;